<template>
  <v-app dark class="tech-app-wrapper">
    <div class="circuit-overlay"></div>
    <div class="scanlines"></div>

    <vue-headful :title="title" />

    <section class="content-relative">
      
      <div class="hero-section-tech d-flex align-center justify-center flex-column">
        <div class="hero-content text-center" style="z-index: 2; width: 100%; max-width: 900px;">
          
          <div class="mb-8 fade-in-up">
            <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon spin-slow">mdi-web-clock</v-icon>
            <h1 class="text-h2 font-weight-black white--text mb-2 neon-text tracking-widest">
              SISTEMA WHOIS .NI
            </h1>
            <div class="subtitle-text cyan--text text--lighten-3 mono-font">
              >> ESCANEO DE DISPONIBILIDAD Y ESTADO DE DOMINIOS_
            </div>
          </div>

          <v-card class="tech-card rounded-lg overflow-hidden elevation-24 pa-2 border-cyan-glow fade-in-up delay-1">
            <v-form @submit.prevent="buscar">
              <v-row no-gutters class="align-center">
                <v-col class="flex-grow-1">
                  <div class="d-flex align-center px-4" style="background: rgba(0,0,0,0.3); height: 64px;">
                    <span class="cyan--text mono-font mr-2 font-weight-bold">root@nic:~$ search</span>
                    <input 
                      v-model="dominio" 
                      type="text" 
                      class="tech-input flex-grow-1 white--text mono-font text-h6"
                      placeholder="ingrese_dominio.com.ni"
                      @keyup.enter="buscar"
                    >
                    <span class="blinking-cursor cyan--text">_</span>
                  </div>
                </v-col>
                <v-col cols="auto" class="pl-2">
                  <v-btn
                    x-large
                    color="cyan accent-4"
                    height="64"
                    width="140"
                    class="black--text font-weight-bold text-h6 neon-bg rounded-0"
                    elevation="0"
                    @click="buscar"
                    :disabled="loading"
                  >
                    <span v-if="!loading" class="d-flex align-center">
                      <v-icon left color="black">mdi-console-line</v-icon> BUSCAR
                    </span>
                    <v-progress-circular v-else indeterminate size="24" width="3" color="black"></v-progress-circular>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card>

        </div>
        <div class="scan-line-hero"></div>
      </div>

      <v-container class="results-container mt-n12 relative z-index-3">
        <v-slide-y-transition mode="out-in">
          
          <div v-if="!data.datos && !notFound && !loading && !apiErrorMsg" key="empty" class="text-center py-12 opacity-50">
            <v-icon size="100" color="cyan darken-3" class="mb-4">mdi-radar</v-icon>
            <h3 class="text-h6 cyan--text text--darken-2 mono-font">>> ESPERANDO COMANDO DE ENTRADA...</h3>
          </div>

          <div v-else key="results">
            
            <v-expand-transition>
              <v-card v-if="apiErrorMsg" class="cyber-card restricted mb-8 elevation-10 fade-in">
                <div class="pa-6 pa-md-8 text-center">
                  <div class="icon-circle restricted-glow mb-4">
                    <v-icon color="#ff1744" size="60" class="pulse-icon">mdi-shield-alert-outline</v-icon>
                  </div>
                  <h2 class="result-title" style="color: #ff1744;">No esta Disponible</h2>
                  <p class="result-subtitle grey--text text--lighten-1 mt-2 mb-6">
                    <!-- El sistema central ha denegado la solicitud para este nodo. -->
                  </p>
                  
                  <div class="alert-glitch-box mx-auto text-left" style="max-width: 600px;">
                    <span class="alert-text font-weight-bold">>_ {{ apiErrorMsg }}</span>
                  </div>
                </div>
              </v-card>
            </v-expand-transition>
            
            <template v-if="!apiErrorMsg && data.datos && !notFound">
              <v-card class="tech-card mb-8 rounded-lg elevation-10 border-top-green">
                <v-card-text class="pa-6">
                  <v-row align="center">
                    <v-col cols="12" md="6">
                      <div class="caption cyan--text text--lighten-2 mono-font mb-1">>> OBJETIVO IDENTIFICADO:</div>
                      <div class="text-h3 font-weight-black white--text text-shadow-blue text-truncate">{{ dominio }}</div>
                    </v-col>
                    <v-col cols="12" md="6" class="text-md-right d-flex align-center justify-md-end gap-3">
                      <v-chip color="green accent-3" class="px-4 py-4 mr-6 neon-chip" label outlined large>
                        <v-icon left color="green accent-3">mdi-check-network</v-icon>
                        <span class="text-h6 font-weight-bold green--text text--accent-3 mono-font">REGISTRADO</span>
                      </v-chip>
                      
                      <div class="text-right">
                        <div class="text-h3 font-weight-bold orange--text text--accent-3 mono-font neon-text-orange">
                          {{ calcularDiasRestantes(data.datos.fechaExpiracion) }}
                        </div>
                        <div class="caption font-weight-bold grey--text mono-font">DÍAS PARA VENCIMIENTO</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-progress-linear value="100" height="2" color="green accent-3" striped class="opacity-50"></v-progress-linear>
              </v-card>

              <v-row>
                <v-col cols="12" md="6">
                  <v-card class="tech-card fill-height rounded-lg elevation-10">
                    <div class="card-header-neon pa-4 d-flex align-center border-bottom-neon">
                      <v-icon color="cyan accent-3" class="mr-3">mdi-office-building-cog</v-icon>
                      <h3 class="cyan--text text-h6 font-weight-bold mono-font">DATOS DEL TITULAR</h3>
                    </div>
                    <v-card-text class="pa-6">
                      <div class="console-box pa-4 mb-6">
                        <div class="caption grey--text mono-font mb-1">ORGANIZACIÓN / CLIENTE</div>
                        <div class="text-h5 white--text font-weight-bold">{{ data.datos.cliente }}</div>
                      </div>
                      <div class="info-row mb-6 d-flex align-center">
                        <v-icon color="orange accent-3" class="mr-3">mdi-clock-alert-outline</v-icon>
                        <div>
                          <div class="caption grey--text mono-font">EXPIRACIÓN DEL DOMINIO</div>
                          <span class="text-h6 font-weight-bold white--text">{{ data.datos.fechaExpiracion }}</span>
                        </div>
                      </div>
                      <div class="info-row d-flex align-start">
                        <v-icon color="blue accent-3" class="mr-3 mt-1">mdi-map-marker-radius</v-icon>
                        <div>
                          <div class="caption grey--text mono-font">UBICACIÓN REGISTRADA</div>
                          <div class="white--text body-1">{{ data.datos.direccion || 'No disponible' }}</div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card class="tech-card fill-height rounded-lg elevation-10">
                    <div class="card-header-neon pa-4 d-flex align-center border-bottom-neon">
                      <v-icon color="cyan accent-3" class="mr-3">mdi-card-account-details-star</v-icon>
                      <h3 class="cyan--text text-h6 font-weight-bold mono-font">CONTACTO ADMIN</h3>
                    </div>
                    <v-card-text class="pa-6">
                      <div class="d-flex align-center mb-6">
                        <v-avatar color="rgba(0, 229, 255, 0.1)" size="70" class="mr-4 neon-border-circle">
                          <span class="cyan--text text-h4 font-weight-bold">
                            {{ contactNameInitial }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="caption grey--text mono-font">IDENTIDAD</div>
                          <div class="text-h6 font-weight-bold white--text text-truncate" style="max-width: 250px;">
                            {{ contactName }}
                          </div>
                          <v-chip x-small color="cyan" outlined class="mt-1">AUTORIZADO</v-chip>
                        </div>
                      </div>
                      <v-divider class="grey darken-3 mb-6"></v-divider>
                      <div class="info-group mb-4">
                        <div class="caption grey--text mono-font mb-1">ENLACE DE CORREO</div>
                        <div class="info-value">
                          <a :href="`mailto:${contactEmail}`" class="text-decoration-none font-weight-medium d-flex align-center tech-link text-h6">
                            <v-icon left small color="cyan">mdi-email-check</v-icon> 
                            {{ contactEmail }}
                          </a>
                        </div>
                      </div>
                      <div class="info-group">
                        <div class="caption grey--text mono-font mb-1">ENLACE DE VOZ</div>
                        <div class="info-value d-flex align-center white--text text-h6">
                          <v-icon small color="green accent-3" class="mr-2">mdi-phone-in-talk</v-icon>
                          {{ contactPhone }}
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </template>

            <v-card v-else-if="!apiErrorMsg && notFound && notFoundMsg" key="error" class="mx-auto mt-8 tech-card border-green elevation-10" max-width="700">
              <div class="pa-8 text-center relative overflow-hidden">
                <div class="absolute-bg-green"></div>
                <v-icon size="100" color="green accent-3" class="mb-4 glow-icon relative z-index-2">mdi-domain-plus</v-icon>
                <h3 class="text-h4 font-weight-black white--text mb-2 relative z-index-2">
                  DOMINIO DISPONIBLE
                </h3>
                <p class="grey--text text--lighten-1 mb-4 mono-font relative z-index-2">
                  >> El Dominio <span class="white--text font-weight-bold">"{{ dominio }}"</span> está libre y listo para ser reclamado.
                </p>
                <p class="white--text mb-8 relative z-index-2">
                  Dirígete a nuestro portal oficial para consultar las opciones de registro, periodos y precios disponibles.
                </p>
                <div class="d-flex justify-center relative z-index-2">
                  <v-btn 
                    href="https://nic.ni/registronuevodominio" 
                    target="_blank"
                    x-large 
                    color="green accent-4" 
                    class="black--text font-weight-bold neon-bg-green glow-on-hover" 
                    depressed
                  >
                    <v-icon left color="black">mdi-cart-outline</v-icon> VER OPCIONES Y PRECIOS
                  </v-btn>
                </div>
              </div>
            </v-card>

          </div>
        </v-slide-y-transition>
      </v-container>
    </section>
  </v-app>
</template>

<script>
import vueHeadful from 'vue-headful';

export default {
  components: { vueHeadful },
  data: () => ({
    title: 'WhoIs .NI | Búsqueda Oficial',
    data: {},
    loading: false,
    dominio: '',
    notFound: false,
    notFoundMsg: '',
    apiErrorMsg: ''
  }),
  computed: {
    contactName() {
      return (this.data && this.data.contactos && this.data.contactos.nombre) ? this.data.contactos.nombre : 'No registrado';
    },
    contactNameInitial() {
      return this.contactName !== 'No registrado' ? this.contactName.charAt(0).toUpperCase() : '?';
    },
    contactPhone() {
      return (this.data && this.data.contactos && this.data.contactos.telefono) ? this.data.contactos.telefono : 'No disponible';
    },
    contactEmail() {
      const contactos = this.data?.contactos;
      if (!contactos || !contactos.correoElectronico) return 'No disponible';

      if (Array.isArray(contactos.correoElectronico)) {
        if (contactos.correoElectronico.length > 0) {
          const primerEmail = contactos.correoElectronico[0];
          return primerEmail.value || primerEmail;
        }
      } else if (typeof contactos.correoElectronico === 'object' && contactos.correoElectronico.value) {
        return contactos.correoElectronico.value;
      } else if (typeof contactos.correoElectronico === 'string') {
        return contactos.correoElectronico;
      }
      return 'No disponible';
    }
  },
  methods: {
    calcularDiasRestantes(fechaExpiracion) {
      if (!fechaExpiracion) return 0;
      const hoy = new Date();
      const vence = new Date(fechaExpiracion);
      hoy.setHours(0,0,0,0);
      vence.setHours(0,0,0,0);
      const diffTime = vence - hoy; 
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0; 
    },
    async buscar() {
      if (!this.dominio) return;
      
      this.loading = true;
      this.notFound = false;
      this.data = {}; 
      this.apiErrorMsg = ""; 

      try {
        const resp = await this.$http.get(this.$keys('WHO_IS') + '?dominio=' + this.dominio);
        
        if (resp.status == 200) {
          if (resp.data.datos && resp.data.datos.fechaExpiracion) {
            let fechaFin = resp.data.datos.fechaExpiracion.split("T", 3);
            resp.data.datos.fechaExpiracion = fechaFin[0];
          }
          this.data = resp.data;
          this.notFound = false;
        }
      } catch (err) {
        this.data = {};
        
        if (err.response) {
          // 1. Capturamos errores de validación (Caracteres y Zonas no permitidas)
          if (err.response.status === 400) {
            let msg = typeof err.response.data === 'string' 
                        ? err.response.data 
                        : err.response.data.title || err.response.data.mensaje || "Formato de dominio inválido.";
            
            this.apiErrorMsg = msg;
            this.notFound = false; 
            this.notFoundMsg = '';
          } 
          // 2. Capturamos error de Lista Negra (Restringido)
          else if (err.response.status === 409) {
            this.apiErrorMsg = err.response.data.mensaje || err.response.data;
            this.notFound = false; 
            this.notFoundMsg = ''; 
          } 
          // 3. Capturamos 404 Not Found (Significa que está libre y disponible)
          else {
            this.notFound = true;
            try {
               this.notFoundMsg = JSON.parse(err.response.data.mensaje);
            } catch (e) {
               this.notFoundMsg = { mensaje: err.response?.data?.mensaje || "Dominio no encontrado o disponible." };
            }
          }
        } else {
          // Falla de red
          this.apiErrorMsg = "Falla de conexión con el servidor.";
          this.notFound = false;
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;600&family=Montserrat:wght@400;700;900&display=swap');

/* --- CONTENEDOR GENERAL --- */
.tech-app-wrapper {
  background-color: #050a14 !important;
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
}
.results-container {
  background-color: transparent; 
}

.mono-font { font-family: 'Fira Code', monospace !important; }
.tracking-widest { letter-spacing: 2px; }

/* Fondos */
.circuit-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background-image: radial-gradient(circle at 50% 20%, rgba(0, 229, 255, 0.05) 0%, transparent 60%);
  z-index: 0;
}
.scanlines {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.1));
  background-size: 100% 4px;
  pointer-events: none; z-index: 1;
}
.content-relative { position: relative; z-index: 2; }

/* Hero Tech */
.hero-section-tech {
  background-image: linear-gradient(to bottom, rgba(5,10,20,0.9), #050a14), url('~@/assets/img/tiscapa.jpg');
  background-size: cover;
  background-position: center;
  min-height: 450px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 60px;
  border-bottom: 1px solid rgba(0, 229, 255, 0.1);
}
.scan-line-hero {
  position: absolute; bottom: 0; width: 100%; height: 2px; background: cyan;
  box-shadow: 0 0 10px cyan;
  animation: scanDown 3s linear infinite; opacity: 0.5;
}

/* Tarjeta Tech y Bordes */
.tech-card, .cyber-card {
  background: rgba(13, 25, 45, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 229, 255, 0.2) !important;
  transition: all 0.3s;
}
.border-top-green { border-top: 4px solid #00E676 !important; }
.border-green { border: 1px solid #00E676 !important; }
.border-cyan-glow { border: 1px solid cyan; box-shadow: 0 0 15px rgba(0, 229, 255, 0.2); }

/* Input Tech (Terminal) */
.tech-input {
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
}
.tech-input::placeholder { color: rgba(255,255,255,0.3); }
.blinking-cursor { font-weight: bold; animation: blink 1s step-end infinite; }

/* Elementos Visuales Generales */
.neon-text { text-shadow: 0 0 10px rgba(0, 229, 255, 0.6); }
.neon-text-orange { text-shadow: 0 0 10px rgba(255, 145, 0, 0.6); }
.text-shadow-blue { text-shadow: 0 0 10px rgba(41, 121, 255, 0.6); }
.glow-icon { filter: drop-shadow(0 0 8px cyan); }

.console-box {
  background: rgba(0,0,0,0.4);
  border-left: 3px solid #00E5FF;
}

.neon-btn { border: 1px solid currentColor; transition: all 0.3s; }
.glow-on-hover:hover { box-shadow: 0 0 20px currentColor; background: rgba(0, 229, 255, 0.1); }
.neon-bg {
  background: linear-gradient(90deg, #00e5ff, #00b8d4) !important;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.4);
}
.neon-bg-green {
  background: linear-gradient(90deg, #00E676, #00C853) !important;
  box-shadow: 0 0 15px rgba(0, 230, 118, 0.4);
}

.tech-link { color: #00E5FF !important; border-bottom: 1px dashed #00E5FF; transition: all 0.2s; }
.tech-link:hover { text-shadow: 0 0 8px cyan; border-bottom-style: solid; }

.neon-chip { border: 1px solid #00E676 !important; box-shadow: 0 0 8px rgba(0, 230, 118, 0.3); background: rgba(0, 230, 118, 0.1) !important; }
.neon-border-circle { border: 2px solid cyan; box-shadow: 0 0 10px rgba(0,255,255,0.3); }

.card-header-neon { background: rgba(0, 229, 255, 0.05); }
.border-bottom-neon { border-bottom: 1px solid rgba(0, 229, 255, 0.2); }

/* --- BLOQUE DE POLÍTICA --- */
.cyber-card.restricted {
  background-color: #09101a !important;
  border-top: 4px solid #ff1744 !important; 
  box-shadow: 0 4px 25px rgba(255, 23, 68, 0.15) !important;
}

.icon-circle {
  display: inline-flex;
  padding: 15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}
.restricted-glow { box-shadow: 0 0 25px rgba(255, 23, 68, 0.4); }

.result-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.result-subtitle { font-size: 1.1rem; }

.alert-glitch-box {
  background: rgba(255, 23, 68, 0.08);
  border: 1px solid rgba(255, 23, 68, 0.4);
  border-left: 4px solid #ff1744;
  padding: 15px 20px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}
.alert-text {
  color: #ffb3b3;
  font-family: 'Fira Code', monospace;
  font-size: 1.1rem;
}

.pulse-icon {
  animation: pulseRed 2s infinite;
}
@keyframes pulseRed {
  0% { text-shadow: 0 0 5px #ff1744; transform: scale(1); }
  50% { text-shadow: 0 0 25px #ff1744; transform: scale(1.1); }
  100% { text-shadow: 0 0 5px #ff1744; transform: scale(1); }
}

.fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Animaciones Generales */
@keyframes spin { 100% { transform: rotate(360deg); } }
.spin-slow { animation: spin 10s linear infinite; }
@keyframes blink { 50% { opacity: 0; } }
@keyframes scanDown { 0% { top: 90%; opacity: 0; } 50% { opacity: 1; } 100% { top: 100%; opacity: 0; } }

/* Utilidades */
.z-index-2 { z-index: 2; position: relative; }
.z-index-3 { z-index: 3; position: relative; }
.absolute-bg-green {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: radial-gradient(circle, rgba(0, 230, 118, 0.1) 0%, transparent 70%);
  z-index: 1;
}
.relative { position: relative; }
</style>